import React from 'react';
import { Row, Col, Spinner } from 'reactstrap';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import KeyFigureStatic from '@/common/charts/KeyFigureStatic';
import { getAnalyticsSummary } from '@/services/analytics/analytics-service';
import { AnalyticsSummaryDto } from '@/services/analytics/analytics-types';

const AnalyticsSummarySection: React.FC = () => {
  const { data, isLoading, error } = useQuery<AnalyticsSummaryDto>({
    queryKey: ['analytics-summary'],
    queryFn: getAnalyticsSummary,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="py-3 text-center">
        <Spinner size="sm" color="primary" />
      </div>
    );
  }

  if (error || !data) {
    return null; // Silently fail to not disrupt the dashboard
  }

  const createMetricItem = (
    value: number,
    label: string,
    icon = 'bx bx-dollar-circle',
    prefix = '$',
    decimals = 2,
  ) => ({
    id: 1,
    cardColor: 'primary',
    label: label,
    badge: '',
    badgeClass: '',
    counter: value,
    bgcolor: 'secondary',
    icon,
    decimals,
    prefix,
    suffix: '',
    separator: ',',
    link: '',
  });

  // Format cycle dates for display
  const formatSafeDate = (dateString: string, fallback: string = 'N/A') => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return fallback;
      }
      return format(date, 'MMM d');
    } catch {
      return fallback;
    }
  };

  const cycleStart = formatSafeDate(data.currentCycleStart);
  const cycleEnd = formatSafeDate(data.currentCycleEnd);
  const cycleLabel = `Current Cycle (${cycleStart} - ${cycleEnd})`;

  return (
    <>
      {/* Rake Metrics Row */}
      <Row className="gy-3 mb-3">
        <Col lg={12}>
          <h5 className="text-muted mb-2">Rake Metrics</h5>
        </Col>
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.todaysRake, "Today's Rake", 'bx bx-trending-up')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.rolling7DaysRake, 'Rolling 7 Days', 'bx bx-calendar')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.currentCycleRake, cycleLabel, 'bx bx-time-five')}
        />
      </Row>

      {/* Live Metrics Row */}
      <Row className="gy-3 mb-4">
        <Col lg={12}>
          <h5 className="text-muted mb-2">Live Metrics</h5>
        </Col>
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.tablesRunning, 'Tables Running', 'bx bx-table', '', 0)}
        />
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.usdtInPlay, 'USDT in Play', 'bx bx-coin')}
        />
        <KeyFigureStatic
          columnProps={{ lg: 4 }}
          item={createMetricItem(data.activePlayers, 'Active Players', 'bx bx-user', '', 0)}
        />
      </Row>

      <hr className="mb-4" />
    </>
  );
};

export default AnalyticsSummarySection;
